import { computed, type MaybeRef, ref, unref } from 'vue';
import type { CreateOpportunityResponse, OpportunityBase, ContactLabel, OpportunityType } from '@apparatix/types/Leads';
import { useUtilities } from './useUtilities';
import { useEndpoint } from './useEndpoint';
import { useContacts } from './useContacts';
import { useStringFunctions } from './useStringFunctions';
import { useOpportunities } from './useOpportunities';
import { AccountSearchFields, useAccounts } from './useAccounts';
import { ContactPayload } from '@apparatix/types/Contacts';
import { PendingAccount, newAccountPayloadTransformer, transformSearchResult } from '@apparatix/schemas/Account';
import {
  LeadAndOpportunityModel, transformStateToPayload, newContactPayloadTransformer, WizardNewContact, transformStateToUpdatePayload,
} from '@apparatix/schemas/CreateLeadWizard';

// TODO - another divergent schema for account data...
type ContactAssociatedAccount = {
  id: number;
  legalName: string;
  formattedType: string;
  owner: {
    id: number;
  }
  rawTypes: ['Advertiser', unknown];
}

type AccountConflicts = {
  pendingAccount: PendingAccount;
  conflicts: PendingAccount[];
}

export const useOpportunityWizard = (accounts: MaybeRef<PendingAccount[]>, contacts: MaybeRef<WizardNewContact[]>, contactLabels: MaybeRef<ContactLabel[]>) => {
  const _accounts = unref(accounts);
  const _contacts = unref(contacts);
  const _contactLabels = unref(contactLabels);
  const accountConflicts = ref<AccountConflicts[]>([]);
  const awaitingDuplicateAccountCheck = ref(false);
  const activeEditingAccount = ref<PendingAccount>();

  const { arrayHasValues } = useUtilities();
  const { createAccount, searchAccounts, getContactsByAdvertiser } = useAccounts();
  const { createContact, getContactLabelChipSeverity } = useContacts();
  const { findUserById, getUserByIdOrDefault } = useOpportunities();
  const { isEmptyString } = useStringFunctions();

  const contactAccountsEndpoint = useEndpoint<{ accounts: ContactAssociatedAccount[] }>({
    type: 'GET',
    successField: 'accounts',
    toast: {
      showOnSuccess: false,
    },
  });

  const createOpportunityEndpoint = useEndpoint<CreateOpportunityResponse>({
    type: 'POST',
    url: '/crm/opportunities',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const updateOpportunityEndpoint = useEndpoint<{ success?: string }>({
    url: '/crm/opportunities',
    type: 'PUT',
    successField: 'success',
    toast: { showOnSuccess: false },
  });

  const primaryContact = computed(() => _contacts?.find(
    contact => arrayHasValues(contact.contactLabels) && contact.contactLabels.map(label => label.contactLabelDisplayName)?.includes('Primary'),
  ));

  const primaryOrDefaultContact = computed(() => {
    if (!arrayHasValues(_contacts)) return undefined;
    if (primaryContact.value) return primaryContact.value;
    if (_contacts[0]) return _contacts[0];
    return undefined;
  });

  const hasAgencyAccount = () => Boolean(_accounts.find(account => account.type === 'Agency'));
  const hasAdvertiserAccount = () => Boolean(_accounts.find(account => account.type === 'Advertiser' || account.type === 'Lead'));

  const getUnusedContactLabels = (thisContactKey: string) => {
    if (!arrayHasValues(_contacts)) return _contactLabels;

    const usedLabels = new Set(
      _contacts.flatMap(contact =>
        arrayHasValues(contactLabels) && contact.key !== thisContactKey
          ? contact.contactLabels?.map(label => label.contactLabelId)
          : [],
      ),
    );

    return _contactLabels?.filter(label => !usedLabels.has(label.contactLabelId));
  };

  const getPrimaryLabel = () => _contactLabels?.find(label => label.contactLabelId === 1);

  const itemAlreadyInArray = <T extends object>(source: T[], predicate: (item: T) => boolean): [boolean, number] => {
    const idx = source?.findIndex(predicate);
    return [idx !== -1, idx];
  };

  const addContact = async (contact: WizardNewContact, skipAccountConflictCheck = false, skipForcedPrimary = false) => {
    const [alreadyAddedContactId] = itemAlreadyInArray(_contacts, c => c.id === contact.id);
    if (alreadyAddedContactId && contact.id) {
      return;
    }

    // if we already have a primary contact don't add the primary label from any new contacts.
    if (primaryContact.value) {
      contact.contactLabels = contact.contactLabels.filter(label => label.contactLabelId !== 1);
    }

    const [alreadyAdded, idx] = itemAlreadyInArray(_contacts, c => c.key === contact.key);
    if (alreadyAdded) {
      const removedCompanyName = !isEmptyString(_contacts[idx].account?.name, true) && isEmptyString(contact.account?.name, true);

      _contacts[idx] = {
        ..._contacts[idx],
        ...contact,
        companyName: removedCompanyName ? undefined : contact.companyName,
      };

      const [hasAccount, accountsIdx] = itemAlreadyInArray(_accounts, account => ('contactKey' in account) && account.contactKey === contact.key);

      if (hasAccount && removedCompanyName) {
        _accounts.splice(accountsIdx, 1);
      }

      if (hasAccount && !isEmptyString(contact.account.name, true)) {
        if (!_accounts[accountsIdx].id) {
          const needsDuplicateCheck = _accounts[accountsIdx].name.toLowerCase() !== contact.account.name.toLowerCase();
          _accounts[accountsIdx].name = contact.account.name;
          if (needsDuplicateCheck) {
            checkForAccountConflicts(_accounts[accountsIdx]);
          }
        }
      }

      return;
    }

    if (!primaryContact.value && !skipForcedPrimary) {
      if (Array.isArray(contact.contactLabels)) {
        contact.contactLabels.push(getPrimaryLabel());
      } else {
        contact.contactLabels = [getPrimaryLabel()];
      }
    }

    if (!contact.id) {
      if (!contact.ownerId) {
        // If an account was added with the contact, we set the contact owner to the user that owns the account
      // otherwise defaults to current user
        const owner = getUserByIdOrDefault(contact.account?.owner?.id);
        contact.ownerId = owner.id;
      }

      if (contact.account?.name) {
        contact.account.contactKey = contact.key;
        addAccount(contact.account, skipAccountConflictCheck);
      }
    }

    if (!arrayHasValues(accounts) && contact.id) {
      const accountsUrl = `/crm/contacts/lonePrimaryOn/${contact.id}`;
      contactAccountsEndpoint.setEndpointUrl(accountsUrl);
      const response = await contactAccountsEndpoint.getResults();

      if (arrayHasValues(response?.accounts)) {
        response.accounts.forEach(account => {
          const accountToAdd = {
            id: account.id,
            type: account.rawTypes.includes('Advertiser') || account.rawTypes.includes('Lead') ? 'Advertiser' : 'Agency',
            displayType: account.formattedType,
            name: account.legalName,
            owner: account.owner,
            key: crypto.randomUUID(),
            contactKey: contact.key,
          };

          _accounts.push(accountToAdd);
        });
      }
    }

    _contacts.push(contact);
  };

  const getAccountContactLabels = (account: PendingAccount & { contactKey?: string }) => {
    if (!account.contactKey) return undefined;

    const contact = _contacts.find(c => c.key === account.contactKey);
    if (!arrayHasValues(contact?.contactLabels)) return undefined;

    return contact.contactLabels.map(label => ({ id: contact.id, labelId: label.contactLabelId }));
  };

  const removeContact = (remove: WizardNewContact) => {
    const [exists, idx] = itemAlreadyInArray(_contacts, c => c.key === remove.key);
    if (exists) {
      _contacts.splice(idx, 1);
      for (let i=0; i<_accounts.length; i++) {
        const account = _accounts[i];
        if ('contactKey' in account) {
          if (account.contactKey === remove.key) {
            _accounts.splice(i, 1);
          }
        }
      }
    }
  };

  const addAccount = async (account: PendingAccount, skipConflictCheck = false) => {
    const [exists, idx] = itemAlreadyInArray(_accounts, a => a.key === account.key);
    if (!exists) {
      if (!account.id && !skipConflictCheck) {
        awaitingDuplicateAccountCheck.value = true;
        await checkForAccountConflicts(account);
      }

      if (!account.owner) {
        account.owner = getUserByIdOrDefault(); // When no id provided, always returns current user
      }

      if (!account.key) {
        account.key = crypto.randomUUID();
      }

      // This is used for display type ONLY, this change is not sent to the backend
      if (account.id && account.type === 'Lead') {
        account.type = 'Advertiser';
      }

      _accounts.push(account);

      if (account.id) {
        await addContactsFromAdvertiser(account.id);
      }
    } else {
      // This hits when the user finishes editing changes to an account in the form in the accounts section
      _accounts[idx] = { ..._accounts[idx], ...account };
      syncAccountChangesWithAssociatedContact(account);
    }
  };

  const addContactsFromAdvertiser = async (accountId: number) => {
    if (accountId) {
      await getContactsByAdvertiser(accountId, results => {
        if (results?.length) {
          results.forEach(result => {
            result.key = crypto.randomUUID();
            addContact(result, false, true);
          });
        }
      });
    }
  };

  const syncAccountChangesWithAssociatedContact = (account: PendingAccount) => {
    if (!account.contactKey) return;

    const [hasContact, idx] = itemAlreadyInArray(_contacts, contact => contact.key === account.contactKey);
    if (!hasContact) {
      return;
    }

    _contacts[idx].companyName = account.name;
    _contacts[idx].account = { ..._contacts[idx].account, ...account };
  };

  const removeAccount = (remove: PendingAccount) => {
    const [exists, idx] = itemAlreadyInArray(_accounts, a => a.key === remove.key);
    if (exists) {
      _accounts.splice(idx, 1);
    }
  };

  const addAccountConflict = (problem: PendingAccount, conflicts: PendingAccount[]) => {
    const [exists, idx] = itemAlreadyInArray(accountConflicts.value, account => account.pendingAccount?.key === problem.key);
    if (exists) {
      accountConflicts.value[idx].conflicts = conflicts;
    } else {
      accountConflicts.value.push({
        pendingAccount: problem,
        conflicts,
      });
    }
  };

  const resolveAccountConflict = (incomingKey: string, selectedKey?: string) => {
    const [exists, idx] = itemAlreadyInArray(accountConflicts.value, account => account.pendingAccount?.key === incomingKey);
    if (!exists) return;

    const selectedAccount = selectedKey
      ? accountConflicts.value[idx].conflicts?.find(account => account.key === selectedKey)
      : accountConflicts.value[idx].pendingAccount;

    selectedAccount.contactKey = accountConflicts.value[idx].pendingAccount.contactKey;

    const replaceIdx = _accounts.findIndex(item => item.key === incomingKey);
    if (replaceIdx !== -1) {
      _accounts.splice(replaceIdx, 1);
    }

    // Always push the account because we always pop it off if its already in the array.
    // We either pop off the new one they tried to create that we are getting rid of now,
    // or this exact account that we push back in.
    _accounts.push(selectedAccount);

    syncAccountChangesWithAssociatedContact(selectedAccount);
    accountConflicts.value.splice(idx, 1);
  };

  const checkForAccountConflicts = (account: PendingAccount) => new Promise<boolean>(resolve => {
    const searchParams: AccountSearchFields = {
      name: account.name,
      mode: 'startswith',
    };

    searchAccounts(searchParams, results => {
      awaitingDuplicateAccountCheck.value = false;

      if (arrayHasValues(results)) {
        const [conflictExistsInAccounts, index] = itemAlreadyInArray(_accounts, act => (act as any).key === account.key);
        if (conflictExistsInAccounts) {
          _accounts.splice(index, 1);
        }
        const pendingConflicts = results.map(result => transformSearchResult(result, findUserById(result.ownerId))) as PendingAccount[];
        addAccountConflict(account, pendingConflicts);
        resolve(true);
      } else {
        resolveAccountConflict(account.key);
        resolve(false);
      }
    });
  });

  const agencyAccountId = computed(() => {
    const agency = _accounts?.find(account => account.type === 'Agency');
    if (agency) {
      return agency.id;
    }

    return undefined;
  });

  const batchProcessModel = async (stateObject: MaybeRef<LeadAndOpportunityModel>, type: OpportunityType, isConvertingLead = false): Promise<OpportunityBase | boolean> => {
    const state = unref(stateObject);

    for (const contact of state.contacts) {
      if (!contact.id) {
        const contactPayload = newContactPayloadTransformer.parse(contact);
        const result = await createContact(contactPayload as ContactPayload);
        if (result?.id) {
          contact.id = result.id;
        } else {
          throw new Error(`Error saving ${contact.fullName} as new Contact`);
        }
      }
    }

    for (const account of state.accounts) {
      const contactIds = getAccountContactLabels(account as PendingAccount);

      // We only update contact labels on new accounts
      if (contactIds && !account.id) {
        // eslint-disable-next-line camelcase
        account.contact_ids = contactIds;
      }

      if (!account.id) {
        if (account.type === 'Advertiser' && type === 'Lead') {
          account.type = 'Lead';
        }

        const payload = newAccountPayloadTransformer.parse(account);
        const result = await createAccount(payload);
        if (result?.id) {
          account.id = result.id;
        } else {
          throw new Error(`Unable to create ${account.name} as new Account. Please try again, or contact support`);
        }
      }
    }

    try {
      if (isConvertingLead) {
        const payload = transformStateToUpdatePayload.parse(state);

        const response = await updateOpportunityEndpoint.getResults(payload);
        if (response?.success) {
          return true;
        }

        return false;
      }

      const payload = transformStateToPayload.parse(state);
      const result = await createOpportunityEndpoint.getResults(payload);

      if (!result?.data?.opportunity) {
        throw new Error(`Unable to create ${state.name}. Please try again, or contact support`);
      }

      return result.data.opportunity;
    } catch {
      throw new Error(`Unable to create ${state.name}. Please try again, or contact support`);
    }
  };

  return {
    accountConflicts,
    activeEditingAccount,
    addAccount,
    addContact,
    addContactsFromAdvertiser,
    agencyAccountId,
    awaitingDuplicateAccountCheck,
    batchProcessModel,
    checkForAccountConflicts,
    getContactLabelChipSeverity,
    getUnusedContactLabels,
    hasAdvertiserAccount,
    hasAgencyAccount,
    primaryOrDefaultContact,
    removeAccount,
    removeContact,
    resolveAccountConflict,
  };
};
